<script lang="ts">
  /** Desktop city-view side rail: turns the wide-screen margin into a useful
   *  empire overview — stats, city alerts, and a quick-switch roster of every
   *  building you own (crew / stock / HEAT at a glance). */
  import { createEventDispatcher } from 'svelte';
  import { worldStore, dispatch } from '@sim/store';
  import { dayOf, districtMeta } from '@sim/config';
  import { heatBand, alerts, num } from '@sim/selectors';

  const emit = createEventDispatcher();
  $: city = $worldStore;
  $: al = alerts(city);

  function heatColor(h: number) {
    const band = heatBand(h);
    return band === 'cool' ? 'var(--heat-cool)' : band === 'warm' ? 'var(--heat-warm)' : band === 'hot' ? 'var(--heat-hot)' : 'var(--heat-crit)';
  }
  function enter(id: number) {
    dispatch({ type: 'switchBuilding', buildingId: id });
    emit('enter', id);
  }
</script>

<div class="panel">
  <div class="phead">
    <h3>🏙️ Your Empire</h3>
    <span class="rep mono">⭐ ×{(1 + city.rep).toFixed(1)}</span>
  </div>

  <div class="stats">
    <div class="stat"><span class="k">Day</span><span class="v mono">{dayOf(city.t)}</span></div>
    <div class="stat"><span class="k">Owned</span><span class="v mono">{city.buildings.length}</span></div>
    <div class="stat"><span class="k">For sale</span><span class="v mono">{city.plots.length}</span></div>
  </div>

  {#if al.length}
    <div class="alerts">
      {#each al as a}<div class="alert {a.kind}">⚠ {a.text}</div>{/each}
    </div>
  {/if}

  <div class="lbl">Buildings</div>
  <div class="blist">
    {#each city.buildings as b (b.id)}
      <button class="brow" class:active={b.id === city.activeId} class:raided={!!b.raid} on:click={() => enter(b.id)} title="Manage {b.name}">
        <div class="btop">
          <span class="bn">{b.name}{#if b.raid}<span class="raidtag"> 🚨</span>{/if}</span>
          <span class="bd">{b.district}</span>
        </div>
        <div class="bmeta mono">
          <span title="crew">👥 {b.colonists.length}/{b.maxCrew}</span>
          <span title="product in stock">📦 {num(b.res.product)}</span>
          <span class="bh" style="color:{heatColor(b.heat)}" title="HEAT">🔥 {b.heat.toFixed(0)}</span>
          <span class="bdemand" title={districtMeta(b.district).blurb}>💲×{districtMeta(b.district).demand.toFixed(2)}</span>
        </div>
      </button>
    {/each}
  </div>

  <p class="tip">Tap a plot on the map to claim new turf.</p>
</div>

<style>
  .panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 14px;
    gap: 9px;
  }
  .phead {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }
  h3 {
    margin: 0;
    font-size: 15px;
  }
  .rep {
    font-size: 12px;
    color: var(--heat-warm);
    font-weight: 700;
  }
  .stats {
    display: flex;
    gap: 7px;
  }
  .stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 7px 4px;
    border-radius: 9px;
    background: var(--panel-2);
    border: 1px solid var(--line);
  }
  .stat .k {
    font-size: 9.5px;
    color: var(--ink-dim);
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .stat .v {
    font-size: 15px;
    font-weight: 800;
    color: var(--ink);
  }
  .alerts {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .alert {
    font-size: 11px;
    padding: 6px 9px;
    border-radius: 8px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink-dim);
  }
  .alert.warn {
    color: var(--heat-warm);
    border-color: color-mix(in srgb, var(--heat-warm) 35%, var(--line));
  }
  .alert.bad {
    color: var(--bad);
    border-color: color-mix(in srgb, var(--bad) 40%, var(--line));
  }
  .lbl {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--ink-faint);
    margin-top: 2px;
  }
  .blist {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .brow {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 9px 10px;
    border-radius: 10px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink);
    text-align: left;
    transition: border-color 0.15s ease;
  }
  .brow:hover {
    border-color: color-mix(in srgb, var(--good) 45%, var(--line));
  }
  .brow.active {
    border-color: var(--good);
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--good) 40%, transparent);
  }
  .brow.raided {
    border-color: var(--heat-crit);
    animation: pulse 0.8s ease-in-out infinite;
  }
  .btop {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .bn {
    font-size: 13px;
    font-weight: 700;
  }
  .raidtag {
    color: var(--heat-crit);
  }
  .bd {
    font-size: 10px;
    color: var(--ink-faint);
  }
  .bmeta {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: var(--ink-dim);
  }
  .bdemand {
    margin-left: auto;
    color: var(--cash);
  }
  .tip {
    font-size: 11px;
    color: var(--ink-faint);
    margin: 4px 0 0;
  }
</style>
